import React from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { visit } from "unist-util-visit";
import { toString as mdastToString } from "mdast-util-to-string";
import type { Components } from "react-markdown";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import { CopyAll, Download } from "@mui/icons-material";
import { useSnackBar } from "../FlowBuilder/Common/SnackBar/index.tsx";
import { useRecoilValue } from "recoil";
import { userInformationAtom } from "../../Services/_states/index.js";

/* =========================================================================
 * 1. Same payload-extraction helpers you already had.
 * ======================================================================= */

function extractFinalResponseText(input: any): string {
  if (!input) return "";
  if (typeof input === "string") return input;

  if (typeof input === "object") {
    if (typeof input?.response === "string") return input.response;
    if (typeof input?.response?.response === "string") return input.response.response;
    if (typeof input?.data?.response?.response === "string") return input.data.response.response;
    if (typeof input?.response?.data?.response?.response === "string")
      return input.response.data.response.response;
    if (typeof input?.response?.output_query === "string") return input.response.output_query;
  }

  return "";
}

function tryParseJSON(text: string): object | null {
  try {
    const parsed = JSON.parse(text);
    // Only treat objects/arrays as "JSON blocks" - plain numbers/strings/bools
    // parse successfully too but shouldn't be rendered as a JSON box.
    if (parsed !== null && typeof parsed === "object") return parsed;
    return null;
  } catch {
    return null;
  }
}

/* =========================================================================
 * 2. Remark plugin that reproduces `maybeBoldProvidePrompt`.
 *    It finds text nodes containing phrases like "please provide" and
 *    bolds everything that comes AFTER the phrase (matching old behavior).
 * ======================================================================= */

const PROVIDE_PHRASES = [
  "please provide your",
  "please provide",
  "kindly provide",
  "please select",
  "kindly select",
];

function remarkBoldAfterProvidePhrase() {
  return (tree: any) => {
    visit(tree, "paragraph", (node: any) => {
      // Only handle simple paragraphs made of a single text node - matches
      // the old line-based logic which only ever looked at whole lines.
      if (!node.children || node.children.length !== 1 || node.children[0].type !== "text") return;

      const line: string = node.children[0].value;
      const lowerLine = line.toLowerCase();

      for (const phrase of PROVIDE_PHRASES) {
        const index = lowerLine.indexOf(phrase);
        if (index !== -1) {
          const before = line.slice(0, index + phrase.length + 1); // include phrase + trailing space
          const after = line.slice(index + phrase.length).trim();
          const afterStart = line.indexOf(after, index + phrase.length);

          const beforeText = line.slice(0, afterStart);
          const afterText = line.slice(afterStart);

          node.children = [
            { type: "text", value: beforeText },
            { type: "strong", children: [{ type: "text", value: afterText }] },
          ];
          break;
        }
      }
    });
  };
}

/* =========================================================================
 * 3. JsonBlock - same copy-to-clipboard box as before, now reused for any
 *    fenced ```json code block AND for the superuser raw-object view.
 * ======================================================================= */

function JsonBlock({ obj }: { obj: any }) {
  const jsonStr = typeof obj === "string" ? obj : JSON.stringify(obj, null, 2);
  const lines = jsonStr.split("\n");
  const { showSnackBar } = useSnackBar();

  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(jsonStr)
      .then(() => showSnackBar("JSON copied to clipboard!", "success"))
      .catch(() => showSnackBar("Failed to copy JSON.", "error"));
  };

  return (
    <Box
      component="pre"
      sx={{
        backgroundColor: "#eee",
        color: "#333",
        p: 2,
        borderRadius: 1,
        overflowX: "auto",
        fontSize: "0.9rem",
        fontFamily: "monospace",
        whiteSpace: "pre-wrap",
        mb: 2,
        border: "1px solid #ccc",
        position: "relative",
      }}
    >
      <IconButton onClick={copyToClipboard} sx={{ position: "absolute", top: 8, right: 8 }} color="primary" size="small">
        <CopyAll fontSize="small" />
      </IconButton>

      {lines.map((line, idx) => {
        let style: React.CSSProperties = {};
        if (/^\s*"/.test(line)) style = { color: "#d14" };
        if (/:\s*".*"/.test(line)) style = { color: "#099" };
        if (/:\s*\d/.test(line)) style = { color: "#905" };
        if (/:\s*(true|false|null)/.test(line)) style = { color: "#069" };

        return (
          <span key={idx} style={style}>
            {line}
            {"\n"}
          </span>
        );
      })}
    </Box>
  );
}

/* =========================================================================
 * 4. Table renderer with CSV download, built from the raw mdast table node
 *    (react-markdown v9 passes `node` on every component prop set).
 * ======================================================================= */

function extractRowsFromTableNode(node: any): string[][] {
  if (!node?.children) return [];
  return node.children.map((row: any) =>
    (row.children || []).map((cell: any) => mdastToString(cell))
  );
}

function MarkdownTable({ node, children }: any) {
  const rows = extractRowsFromTableNode(node);

  const downloadCsv = () => {
    const csvData = rows.map((row) => row.map((c) => `"${c.replace(/"/g, '""')}"`).join(",")).join("\n");
    const blob = new Blob([csvData], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = "table.csv";
    link.click();
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Tooltip title="Download Table as CSV">
        <Button variant="contained" color="primary" size="small" onClick={downloadCsv} sx={{ mb: 1 }}>
          <Download /> Download CSV
        </Button>
      </Tooltip>
      <Table
        size="small"
        aria-label="markdown table"
        sx={{ mb: 1, border: "1px solid #ccc", bgcolor: "#eee", borderRadius: 1 }}
      >
        {children}
      </Table>
    </Box>
  );
}

/* =========================================================================
 * 5. Code renderer - fenced ```json blocks become JsonBlock, everything
 *    else (inline `code` or other fenced blocks) keeps the old monospace
 *    chip styling.
 * ======================================================================= */

function MarkdownCode({ inline, className, children }: any) {
  const raw = String(children).replace(/\n$/, "");
  const lang = /language-(\w+)/.exec(className || "")?.[1];

  if (!inline) {
    if (lang === "json") {
      const parsed = tryParseJSON(raw);
      if (parsed) return <JsonBlock obj={parsed} />;
    }
    // Non-JSON fenced block: still try, since the original component
    // detected JSON purely by looking at leading { / [ with no fence.
    const parsed = tryParseJSON(raw);
    if (parsed) return <JsonBlock obj={parsed} />;

    return (
      <Box
        component="pre"
        sx={{
          backgroundColor: "#f4f4f4",
          p: 2,
          borderRadius: 1,
          overflowX: "auto",
          fontSize: "0.9rem",
          fontFamily: "monospace",
          whiteSpace: "pre-wrap",
          mb: 2,
        }}
      >
        {raw}
      </Box>
    );
  }

  return (
    <Box component="code" sx={{ fontFamily: "monospace", bgcolor: "#f4f4f4", px: "4px", borderRadius: "4px" }}>
      {raw}
    </Box>
  );
}

/* =========================================================================
 * 6. Component map passed to ReactMarkdown - reproduces headings, bold,
 *    italic, links, lists, paragraphs with the same MUI look & feel.
 * ======================================================================= */

const HEADING_VARIANT: Record<number, "h6" | "subtitle1" | "subtitle2"> = {
  1: "h6",
  2: "h6",
  3: "subtitle1",
  4: "subtitle2",
  5: "subtitle2",
  6: "subtitle2",
};

function makeComponents(): Components {
  return {
    h1: ({ children }) => <Typography variant={HEADING_VARIANT[1]} sx={{ fontWeight: "bold", mb: 1 }}>{children}</Typography>,
    h2: ({ children }) => <Typography variant={HEADING_VARIANT[2]} sx={{ fontWeight: "bold", mb: 1 }}>{children}</Typography>,
    h3: ({ children }) => <Typography variant={HEADING_VARIANT[3]} sx={{ fontWeight: "bold", mb: 1 }}>{children}</Typography>,
    h4: ({ children }) => <Typography variant={HEADING_VARIANT[4]} sx={{ fontWeight: "bold", mb: 1 }}>{children}</Typography>,
    h5: ({ children }) => <Typography variant={HEADING_VARIANT[5]} sx={{ fontWeight: "bold", mb: 1 }}>{children}</Typography>,
    h6: ({ children }) => <Typography variant={HEADING_VARIANT[6]} sx={{ fontWeight: "bold", mb: 1 }}>{children}</Typography>,

    p: ({ children }) => (
      <Typography variant="body1" sx={{ whiteSpace: "pre-wrap", mb: 1 }} component="p">
        {children}
      </Typography>
    ),

    strong: ({ children }) => (
      <Typography component="span" sx={{ fontWeight: "bold", display: "inline" }}>
        {children}
      </Typography>
    ),

    em: ({ children }) => (
      <Typography component="span" sx={{ fontStyle: "italic", display: "inline" }}>
        {children}
      </Typography>
    ),

    a: ({ href, children }) => (
      <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: "#1a73e8", textDecoration: "underline" }}>
        {children}
      </a>
    ),

    ul: ({ children }) => (
      <Box component="ul" sx={{ pl: { xs: 2, sm: 2, md: 4 }, mb: 2 }}>
        {children}
      </Box>
    ),
    ol: ({ children }) => (
      <Box component="ol" sx={{ pl: { xs: 2, sm: 2, md: 4 }, mb: 2 }}>
        {children}
      </Box>
    ),
    li: ({ children }) => (
      <li style={{ marginBottom: "4px" }}>
        <Typography component="span" variant="body1">
          {children}
        </Typography>
      </li>
    ),

    table: (props: any) => <MarkdownTable {...props} />,
    thead: ({ children }) => <TableHead>{children}</TableHead>,
    tbody: ({ children }) => <TableBody>{children}</TableBody>,
    tr: ({ children }) => <TableRow>{children}</TableRow>,
    th: ({ children }) => <TableCell sx={{ fontWeight: "bold", bgcolor: "#ddd" }}>{children}</TableCell>,
    td: ({ children }) => <TableCell>{children}</TableCell>,

    code: (props: any) => <MarkdownCode {...props} />,

    hr: () => <Box sx={{ height: 8 }} />,
  };
}

/* =========================================================================
 * 7. Public component - drop-in replacement for <BeautifyText />.
 * ======================================================================= */

interface BeautifyMarkdownProps {
  text: any;
  title?: string;
}

export function BeautifyMarkdown({ text: rawInput, title }: BeautifyMarkdownProps) {
  const loggedInUser = useRecoilValue(userInformationAtom);
  const isSuperuser = !!loggedInUser?.is_superuser;

  const effective = isSuperuser ? rawInput : extractFinalResponseText(rawInput);

  // Superuser + raw object -> full JSON view, same as before.
  if (isSuperuser && typeof effective === "object" && effective !== null) {
    return (
      <Box sx={{ mb: 0, bgcolor: "#eeeeee00", p: { xs: 1, sm: 1, md: 1 }, borderRadius: 1 }}>
        {title && <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>}
        <JsonBlock obj={effective} />
      </Box>
    );
  }

  // Superuser + JSON-parseable string -> JSON view.
  if (isSuperuser && typeof effective === "string") {
    const parsed = tryParseJSON(effective);
    if (parsed) {
      return (
        <Box sx={{ mb: 0, bgcolor: "#eeeeee00", p: { xs: 1, sm: 1, md: 1 }, borderRadius: 1 }}>
          {title && <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>}
          <JsonBlock obj={parsed} />
        </Box>
      );
    }
  }

  const text = typeof effective === "string" ? effective : JSON.stringify(effective || "");

  // Whole-input JSON (non-superuser, but response itself is a JSON blob
  // with no surrounding prose) -> also show as a JsonBlock, matching the
  // old inline "{" / "[" line detection.
  const wholeParsed = tryParseJSON(text.trim());
  if (wholeParsed) {
    return (
      <Box sx={{ mb: 0, bgcolor: "#eeeeee00", p: { xs: 1, sm: 1, md: 1 }, borderRadius: 1 }}>
        {title && <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>}
        <JsonBlock obj={wholeParsed} />
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 0, bgcolor: "#eeeeee00", p: { xs: 1, sm: 1, md: 1 }, borderRadius: 1 }}>
      {title && <Typography variant="h6" sx={{ mb: 1 }}>{title}</Typography>}
      <ReactMarkdown remarkPlugins={[remarkGfm, remarkBoldAfterProvidePhrase]} components={makeComponents()}>
        {text}
      </ReactMarkdown>
    </Box>
  );
}

export default BeautifyMarkdown;